/**
 * How this app spells an address, and how it reads one back.
 *
 * Every screen that links anywhere builds the link here rather than with
 * string arithmetic of its own, because a URL is a contract with the reload
 * button and with every notification sitting in somebody's inbox: two
 * spellings of one rule is how a link gains a `+` where it needed a `%20` on
 * one screen and not another. The segment names and parameter keys are in
 * `routes.constants.ts`; the route table that mounts screens is the router's.
 *
 * Reading goes the other way with no trust at all. `parseRole` and
 * `matchWorkspace` match a path against what the server derived from real
 * grants, so a hand-edited URL resolves to null rather than to a workspace the
 * caller was never given.
 */

import { ROLE_KEYS } from "@ondo/shared";
import type { RoleKey, WorkspaceRef } from "@ondo/shared";

import type { WorkTabId } from "../work/work.constants.ts";
import { FOCUS_PARAMS, PERSONAL_SEGMENT } from "./routes.constants.ts";

/**
 * Which organisation a workspace URL names.
 *
 * The ONDO id rather than a slug of the display name: it is stable, already
 * shown in the interface, and two entities may share a name. A personal
 * workspace has no organisation, so it gets the reserved segment instead.
 */
export function orgSegment(workspace: WorkspaceRef): string {
  return workspace.organisation?.ondoId ?? PERSONAL_SEGMENT;
}

/**
 * "/gallery/ONDO-6624/dashboard", or "/gallery/ONDO-6624" with no destination.
 *
 * Role *and* organisation, because an account can hold two workspaces of the
 * same role — two galleries are two workspaces — and "/gallery/dashboard"
 * could not say which. The whole point of putting this in the URL is that a
 * reload lands where the tab was.
 */
export function workspacePath(workspace: WorkspaceRef, destination?: string): string {
  const base = `/${workspace.role}/${orgSegment(workspace)}`;
  return destination ? `${base}/${destination}` : base;
}

/**
 * '/collector/me/work/1d0c…'.
 *
 * Named rather than a third argument to `workspacePath`, so a caller cannot
 * hand a record to a destination that takes none — `work` is the only one that
 * does, and its screen is the only thing that should be building this.
 */
export function workPath(
  workspace: WorkspaceRef,
  workId: string,
  /**
   * Not exclusive, unlike `verificationsPath`'s two. A tab and a link are
   * different questions — which reading of the record, and which row in it —
   * and a request answered on a condition report is honestly both.
   */
  focus?: { entry?: string; tab?: WorkTabId },
): string {
  const base = `${workspacePath(workspace, "work")}/${workId}`;
  // Hand-built rather than URLSearchParams.toString(), which encodes a space as
  // '+'. An entry id is opaque and a URL that survives a round trip is the whole
  // point of this function; the test on `entry: "e 9"` pins it.
  const query = [
    focus?.entry ? `${FOCUS_PARAMS.entry}=${encodeURIComponent(focus.entry)}` : null,
    focus?.tab ? `${FOCUS_PARAMS.tab}=${encodeURIComponent(focus.tab)}` : null,
  ].filter((part) => part !== null);

  return query.length === 0 ? base : `${base}?${query.join("&")}`;
}

/**
 * '?entry=1d0c…', for a caller that already holds the path.
 *
 * The dashboard is that caller: it is handed a `workBase` string rather than a
 * `WorkspaceRef`, so it cannot use `workPath` above and would otherwise repeat
 * the encode. Exported so the encoding rule has ONE definition — a `+` for a
 * space here would break the same links `workPath`'s test exists to protect.
 */
export function entryFocusQuery(entryId: string): string {
  return `?${FOCUS_PARAMS.entry}=${encodeURIComponent(entryId)}`;
}

/**
 * Where an activity row points, or nothing.
 *
 * ONLY A WORK HAS A SCREEN OF ITS OWN. A collection does not — there is no
 * per-collection route — so a row about one is text and not a link, and this
 * returns null rather than inventing an address for it.
 *
 * A verification lands on the ENTRY it was about, which is what `entryId` is
 * carried for: `?entry=` is what the work screen reads to open the tab holding
 * every kind and scroll to that row. Without it, a reader told that a condition
 * report was disputed arrives at the top of a chain of fourteen links and has
 * to find it themselves.
 *
 * The link is a SNAPSHOT'S link. The work may since have moved or stopped being
 * visible to the reader, so it can land on the record's error state — which is
 * correct: an audit line records what happened, not what is still permitted.
 *
 * Lives here because both the activity screen and the dashboard render the same
 * feed. They had a private copy each — `hrefOf` and `hrefFor`, identical but
 * for the shape of one ternary — which is the pair `profile.model.ts` argues
 * against by name. Two spellings of one URL rule is how a link gains a `+`
 * where it needed a `%20` on one screen and not the other.
 */
export function activityHref(
  entry: { subject: { kind: string; id: string }; entryId: string | null },
  workBase: string,
): string | null {
  if (entry.subject.kind !== "work") return null;

  const base = `${workBase}/${entry.subject.id}`;

  return entry.entryId === null ? base : `${base}${entryFocusQuery(entry.entryId)}`;
}

/**
 * One search parameter changed, everything else on the address kept.
 *
 * Pure, and here rather than inline in a view, so "changing the tab must not
 * drop `?entry=`" is a rule with a test rather than a line of URL arithmetic in
 * a component. `set` and not `append`, which would produce '?tab=a&tab=b'; null
 * removes the key, which is how a default state keeps the plain address.
 */
export function withParam(
  current: URLSearchParams,
  key: string,
  value: string | null,
): URLSearchParams {
  const next = new URLSearchParams(current);
  if (value === null) next.delete(key);
  else next.set(key, value);
  return next;
}

/**
 * '/collector/me/verifications?request=8f2c…', or the screen with nothing focused.
 *
 * Exactly one of the two, because they are two tabs: the screen infers which to
 * open from which parameter it was given, rather than taking a third that could
 * disagree with both. One authority for one fact.
 *
 * The id is not validated here and is not meant to be. A stale or invented one
 * is a URL that lands on the screen with a sentence saying the thing is not
 * there — which is the right outcome for a link that has been sitting in
 * somebody's inbox for a month, and not something a path builder can know.
 */
export function verificationsPath(
  workspace: WorkspaceRef,
  focus?: { request: string } | { claim: string } | { representation: string },
): string {
  const base = workspacePath(workspace, "verifications");
  if (!focus) return base;

  const [key, id] =
    "request" in focus
      ? [FOCUS_PARAMS.request, focus.request]
      : "claim" in focus
        ? [FOCUS_PARAMS.claim, focus.claim]
        : [FOCUS_PARAMS.representation, focus.representation];

  return `${base}?${key}=${encodeURIComponent(id)}`;
}

/**
 * Null for anything that is not a role.
 *
 * Exact match, like `parseDestination`: a URL is client-controlled input, and
 * being lenient here is how a hand-edited path resolves to something the caller
 * did not ask for.
 */
export function parseRole(raw: string | undefined): RoleKey | null {
  return (ROLE_KEYS as readonly string[]).includes(raw ?? "") ? (raw as RoleKey) : null;
}

/**
 * Which of the caller's workspaces a URL names, or null for none.
 *
 * Matched against the list the server derived from real grants, never trusted
 * from the path — so a hand-edited `/verifier/ONDO-6624` resolves to null for
 * an account without that grant, exactly as an unknown one does. The URL says
 * where to look, not what the caller may open.
 */
export function matchWorkspace(
  workspaces: readonly WorkspaceRef[],
  role: string | undefined,
  org: string | undefined,
): WorkspaceRef | null {
  const parsed = parseRole(role);
  if (!parsed || !org) return null;

  return (
    workspaces.find(
      (workspace) => workspace.role === parsed && orgSegment(workspace) === org,
    ) ?? null
  );
}
